import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

export interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  content: string;
  targetType: 'ALL_PATRONS' | 'SPECIFIC_TIERS' | 'ACTIVE_ONLY' | 'NEW_PATRONS' | 'CHURNED_PATRONS';
  tierIds: string[];
  status: 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELLED';
  scheduledFor?: string;
  sentAt?: string;
  totalSent: number;
  totalOpens: number;
  totalClicks: number;
  totalBounces: number;
  totalUnsubscribes: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  isPublic: boolean;
  usageCount: number;
  createdAt: string;
}

export interface CampaignAnalytics {
  campaign: EmailCampaign;
  stats: {
    totalSent: number;
    totalOpens: number;
    totalClicks: number;
    totalBounces: number;
    totalUnsubscribes: number;
    openRate: number;
    clickRate: number;
    clickThroughRate: number;
    bounceRate: number;
    unsubscribeRate: number;
  };
  sends: Array<{
    status: string;
    sentAt?: string;
    openedAt?: string;
    clickedAt?: string;
    bouncedAt?: string;
    unsubscribedAt?: string;
  }>;
}

export interface CreateCampaignData {
  name: string;
  subject: string;
  content: string;
  targetType: string;
  tierIds?: string[];
  scheduledFor?: string;
}

class EmailCampaignService {
  private getAuthHeader() {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }

  async createCampaign(data: CreateCampaignData): Promise<EmailCampaign> {
    const response = await axios.post(`${API_URL}/email-campaigns`, data, {
      headers: this.getAuthHeader(),
    });
    return response.data.campaign;
  }

  async listCampaigns(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ campaigns: EmailCampaign[]; total: number }> {
    const response = await axios.get(`${API_URL}/email-campaigns`, {
      headers: this.getAuthHeader(),
      params,
    });
    return response.data;
  }

  async getCampaignAnalytics(campaignId: string): Promise<CampaignAnalytics> {
    const response = await axios.get(`${API_URL}/email-campaigns/${campaignId}`, {
      headers: this.getAuthHeader(),
    });
    return response.data;
  }

  async updateCampaign(
    campaignId: string,
    data: Partial<CreateCampaignData>
  ): Promise<EmailCampaign> {
    const response = await axios.put(`${API_URL}/email-campaigns/${campaignId}`, data, {
      headers: this.getAuthHeader(),
    });
    return response.data.campaign;
  }

  async deleteCampaign(campaignId: string): Promise<void> {
    await axios.delete(`${API_URL}/email-campaigns/${campaignId}`, {
      headers: this.getAuthHeader(),
    });
  }

  async sendCampaign(campaignId: string): Promise<{
    totalSent: number;
    totalFailed: number;
  }> {
    const response = await axios.post(
      `${API_URL}/email-campaigns/${campaignId}/send`,
      {},
      {
        headers: this.getAuthHeader(),
      }
    );
    return response.data;
  }

  async cancelCampaign(campaignId: string): Promise<EmailCampaign> {
    const response = await axios.post(
      `${API_URL}/email-campaigns/${campaignId}/cancel`,
      {},
      {
        headers: this.getAuthHeader(),
      }
    );
    return response.data.campaign;
  }

  async duplicateCampaign(campaignId: string): Promise<EmailCampaign> {
    const response = await axios.post(
      `${API_URL}/email-campaigns/${campaignId}/duplicate`,
      {},
      {
        headers: this.getAuthHeader(),
      }
    );
    return response.data.campaign;
  }

  async listTemplates(): Promise<EmailTemplate[]> {
    const response = await axios.get(`${API_URL}/email-campaigns/templates/list`, {
      headers: this.getAuthHeader(),
    });
    return response.data.templates;
  }

  async createTemplate(data: {
    name: string;
    subject: string;
    content: string;
    isPublic?: boolean;
  }): Promise<EmailTemplate> {
    const response = await axios.post(`${API_URL}/email-campaigns/templates`, data, {
      headers: this.getAuthHeader(),
    });
    return response.data.template;
  }

  async useTemplate(templateId: string): Promise<EmailTemplate> {
    const response = await axios.post(
      `${API_URL}/email-campaigns/templates/${templateId}/use`,
      {},
      {
        headers: this.getAuthHeader(),
      }
    );
    return response.data.template;
  }
}

export default new EmailCampaignService();
